import { ApiError, changeNow } from './api'
import './whatsapp-card.css'
import { el, toast } from './ui'
import { inStoreApp } from './store-app'

// Extra features > "WhatsApp from Roundly" (0081; Liam, 21 Oct 2026: "reply to customers in app ... through WhatsApp", and
// "the extra features as a way of onboarded this"; docs/plans/whatsapp-direct/PLAN.md, pictures 1-4 at
// https://claude.ai/artifact/PdC9wPAwE4rkQepWP8LXFz). The round's own WhatsApp Business number connected through Meta's
// window, once: the phone keeps working as now, and Roundly sends and receives on the same number. Not connected: what it
// does, what you need, Connect WhatsApp (in the iPhone app: connect on a computer or in Safari, because Meta's window can't
// come back into the app). Connected: the number, the 14 days, voice notes written out, Disconnect. Needs you: what went
// wrong in plain words, and Check again.

export const WA_PATH = '/api/business/whatsapp-meta'
export interface WaCard {
  offered: boolean
  words_possible: boolean
  connection: null | {
    status: 'connecting' | 'connected' | 'dropped' | 'disconnecting'
    number: string; on_biz_app: boolean; problem: string | null; problem_kind: string | null
    voice_words: boolean; sending_off: boolean; chases_paused_at: string | null; connected_at: string | null; version: number
  }
  history: { state: string; progress: number | null } | null
  voice_notes: number
  // Payment messages and reminders from Roundly (Stage 2).
  payments?: null | {
    offer_cycles?: [number, number] | null; cycles_version?: number
    statement_at: string | null; statement_version?: number | null; rule_version?: number; send_from: 'roundly' | 'phone'; paused: boolean; sent_this_month: number
    wordings: { kind: string; title: string; state: 'approved' | 'waiting' | 'refused' | 'paused' | 'needs_change' | 'not_sent'; reason: string | null }[]
  }
}

// The owner's statement (PLAN.md item 72), version 3 (0106): Book again and enquiry regular-clean offers; STOP still stops WhatsApps.
const STATEMENT = "my customers and enquirers whose way is WhatsApp gave me their number to be messaged there about their cleans and payments. Roundly only sends offers about their cleaning: a Book again message after a new customer's first clean, or an offer of regular cleans on an enquiry. They can choose just occasionally, or reply STOP to stop WhatsApps from Roundly."
const STATE_WORDS: Record<string, string> = { approved: 'Approved', waiting: 'Waiting for Meta', refused: 'Refused', paused: 'Paused by Meta', needs_change: 'Needs a change', not_sent: 'Not sent yet' }

// Payment messages and reminders from Roundly (Stage 2; PLAN.md item 72, picture "WhatsApp from Roundly, connected"): the
// owner's statement once, then each wording as Meta sees it, this month's count, and whether Send goes from Roundly.
function paymentsPart(p: NonNullable<WaCard['payments']>, note: HTMLElement, reload: () => void): HTMLElement {
  const box = el('div', { class: 'wa-payments' })
  box.append(cyclesPart(p, note, reload))
  if (!p.statement_at) {
    box.append(el('strong', {}, 'Payment messages and reminders from Roundly'),
      el('p', { class: 'muted' }, "Owed's payment message and reminders can go from your work number on WhatsApp, straight from Roundly, to customers whose way is WhatsApp. Meta checks your wording first, which can take up to a day. Meta charges a few pence for each one."),
      el('p', {}, `By switching this on you say: ${STATEMENT}`))
    const yes = el('button', { type: 'button', class: 'btn-primary' }, 'Switch on and send my wording to Meta') as HTMLButtonElement
    yes.addEventListener('click', () => { yes.disabled = true; yes.textContent = 'Sending to Meta…'; void act({ action: 'statement', rule_version: p.rule_version ?? 1 }, note, reload, () => toast('Your wording has gone to Meta. Owed sends from Roundly once each is approved.')) })
    box.append(yes)
    return box
  }
  // Agreed before Book again could go on WhatsApp (0099): the one new line, agreed once more, before its wording goes to Meta.
  if ((p.statement_version ?? 1) < (p.rule_version ?? 1)) {
    const add = el('button', { type: 'button', class: 'btn-secondary' }, 'Add cleaning offers and send the wording to Meta') as HTMLButtonElement
    add.addEventListener('click', () => { add.disabled = true; add.textContent = 'Sending to Meta…'; void act({ action: 'statement', rule_version: p.rule_version }, note, reload, () => toast('Book again has gone to Meta. Once approved, Book again can send it from Roundly.')) })
    box.append(el('div', { class: 'wa-book-again-add' },
      el('strong', {}, 'Cleaning offers from Roundly'),
      el('p', { class: 'muted' }, "After a new customer's first clean, or on an enquiry with a price, you can offer two of your round's own cycles or Just occasionally. Their tap saves their choice. You arrange the day. Meta checks the wording first and charges for each offer."),
      el('p', {}, `By adding it you say: ${STATEMENT}`), add))
  }
  const sw = toggle(p.send_from === 'roundly', 'Send from Roundly')
  sw.addEventListener('click', () => { sw.disabled = true; void act({ action: 'send_from', on: p.send_from !== 'roundly' }, note, reload) })
  box.append(el('div', { class: 'wa-voice' },
    el('div', {}, el('strong', {}, 'Send from Roundly'),
      el('p', { class: 'muted' }, p.send_from === 'roundly' ? "Owed's Send and Send all go from Roundly on WhatsApp, once Meta has approved the words." : 'Send opens WhatsApp on your phone, as before.')),
    sw))
  if (p.paused) box.append(el('p', { class: 'wa-attention' }, "Meta has lowered your number's rating, so reminders from Roundly are paused. Replies still go."))
  const list = el('div', { class: 'wa-wordings' }, el('div', { class: 'wa-wordings-head' }, 'Your wording, as Meta sees it'))
  for (const w of p.wordings) {
    list.append(el('div', { class: `wa-wording wa-wording-${w.state}` },
      el('div', { class: 'wa-wording-top' }, el('span', {}, w.title), el('span', { class: 'wa-wording-state' }, STATE_WORDS[w.state] ?? w.state)),
      ...(w.reason && w.state !== 'approved' ? [el('span', { class: 'muted wa-small' }, w.reason)] : [])))
  }
  box.append(list,
    el('p', { class: 'muted wa-small' }, 'Change the words in More, Your messages and they go to Meta again. Until Meta says yes, that Send opens WhatsApp.'),
    el('p', { class: 'muted wa-small' }, `${p.sent_this_month} sent from Roundly this month. Meta charges at most about 2p each.`))
  return box
}

function cyclesPart(p: NonNullable<WaCard['payments']>, note: HTMLElement, reload: () => void): HTMLElement {
  const a = el('input', { type: 'number', min: '1', max: '52', step: '1', 'aria-label': 'First cycle, weeks', class: 'wa-cycle-a' }) as HTMLInputElement
  const b = el('input', { type: 'number', min: '1', max: '52', step: '1', 'aria-label': 'Second cycle, weeks', class: 'wa-cycle-b' }) as HTMLInputElement
  a.value = p.offer_cycles ? String(p.offer_cycles[0]) : ''; b.value = p.offer_cycles ? String(p.offer_cycles[1]) : ''
  const save = el('button', { type: 'button', class: 'btn-secondary wa-cycles-save' }, p.statement_version === p.rule_version ? 'Save cycles and send for approval' : 'Save cycles') as HTMLButtonElement
  save.addEventListener('click', () => {
    const first = Number(a.value), second = Number(b.value)
    if (!Number.isInteger(first) || !Number.isInteger(second) || first < 1 || second > 52 || first >= second) { note.textContent = 'Choose two different whole numbers of weeks, shortest first.'; return }
    save.disabled = true
    void act({ action: 'offer_cycles', a: first, b: second, cycles_version: p.cycles_version }, note, reload,
      () => toast(p.statement_version === p.rule_version ? 'Cycles saved. The wording goes to Meta again.' : 'Cycles saved. Agree to the statement to send the wording to Meta.'))
  })
  return el('section', { class: 'wa-offer-cycles' }, el('h4', {}, 'The two cycles on your buttons'),
    el('div', { class: 'wa-cycles-fields' }, el('label', {}, 'First cycle, weeks', a), el('label', {}, 'Second cycle, weeks', b)),
    el('p', { class: 'muted wa-small' }, 'Book again and Offer regular cleans use these two cycles. Prices stay in the message. Changing the cycles sends both wordings to Meta again. Offer regular cleans stays hidden until approved.'), save)
}

const shownNumber = (e164: string): string => e164.replace(/^\+44(\d{4})(\d{6})$/, '0$1 $2').replace(/^\+1(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3')
const inIphoneApp = (): boolean => !!(window as unknown as { RoundlyIOS?: unknown }).RoundlyIOS

function chatIcon(): HTMLElement {
  const box = el('span', { class: 'feature-icon', 'aria-hidden': 'true' })
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('width', '26'); svg.setAttribute('height', '26')
  for (const d of ['M4 20l1.3-3.9A8 8 0 1 1 8 19.1z', 'M9 10h6M9 13.5h4']) {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    path.setAttribute('d', d); path.setAttribute('fill', 'none'); path.setAttribute('stroke', 'currentColor'); path.setAttribute('stroke-width', '1.8'); path.setAttribute('stroke-linecap', 'round'); path.setAttribute('stroke-linejoin', 'round')
    svg.append(path)
  }
  box.append(svg)
  return box
}

function toggle(on: boolean, label: string): HTMLButtonElement {
  return el('button', { type: 'button', role: 'switch', class: `mb-switch feature-switch${on ? ' mb-switch-on' : ''}`, 'aria-checked': on ? 'true' : 'false', 'aria-label': label },
    el('span', { class: 'mb-switch-knob' }, el('span', { class: 'feature-tick', 'aria-hidden': 'true' }, '✓'))) as HTMLButtonElement
}

async function act(body: Record<string, unknown>, note: HTMLElement, done: () => void, ok?: () => void): Promise<boolean> {
  note.textContent = ''
  try {
    await changeNow('POST', WA_PATH, body)
    ok?.()
    done()
    return true
  } catch (err) {
    note.textContent = err instanceof ApiError ? err.message : "That didn't go through. Check your signal and try again."
    // The card is read again, so what it says matches what happened; the words stay above it.
    done()
    return false
  }
}

export function whatsappCard(row: WaCard, reload: () => void): HTMLElement {
  const c = row.connection
  const on = c?.status === 'connected' && !c.sending_off
  const attention = !!c && (c.status === 'dropped' || c.sending_off || (c.status === 'connecting' && !!c.problem))
  const card = el('section', { class: `feature-card${on && !attention ? ' feature-card-on' : ''}${attention ? ' feature-card-attention' : ''}`, 'aria-labelledby': 'feature-whatsapp' })
  const status = !c ? 'Off' : attention ? 'Needs you' : c.status === 'connecting' ? 'Connecting' : c.status === 'disconnecting' ? 'Disconnecting' : 'On'
  card.append(el('div', { class: 'feature-top' },
    chatIcon(),
    el('div', { class: 'feature-titles' },
      el('h3', { id: 'feature-whatsapp' }, 'WhatsApp from Roundly'),
      el('p', {}, c ? `Connected: ${shownNumber(c.number)}` : "Reply to customers' WhatsApps here, on your own work number. Your phone keeps working as now.")),
    el('div', { class: 'feature-control' }, el('span', { class: 'feature-status' }, status))))
  const note = el('p', { class: 'form-error', role: 'alert' })

  // Inside the iPhone or Android app (Liam, 4 Nov 2026: "Go with the second"): set up on a computer, with no prices or
  // card talk in the app (docs/STORE-APPS.md, privacy-and-review.md risk 6).
  if (!c && inStoreApp()) {
    card.append(el('div', { class: 'wa-elsewhere' }, el('strong', {}, 'Set this up on a computer'),
      el('p', {}, 'Go to app.roundly.work on a computer, then More, Extra features.'),
      el('p', { class: 'muted' }, "Once it's connected, everything works here too.")))
    card.append(note, el('div', { class: 'feature-where' }, el('strong', {}, 'Where it appears'), el('span', {}, 'Chats, and what customers wrote back on Owed.')))
    return card
  } else if (!c) {
    card.append(el('div', { class: 'wa-need' }, el('strong', {}, 'What you need'),
      el('ol', {},
        el('li', {}, 'WhatsApp Business on the work phone (the green icon with a B).'),
        el('li', {}, 'A Facebook login.'),
        el('li', {}, 'A card in Meta for its charges. Replies are free up to 1,000 a month; after that, and for payment messages, a few pence each. ',
          el('a', { href: 'https://developers.facebook.com/docs/whatsapp/pricing', target: '_blank', rel: 'noopener' }, "Meta's prices")))))
    if (inIphoneApp()) {
      card.append(el('div', { class: 'wa-elsewhere' }, el('strong', {}, 'Connect it on a computer'),
        el('p', {}, 'Or on your phone in Safari: go to app.roundly.work, then More, Extra features.'),
        el('p', { class: 'muted' }, "Meta's window can't open inside the Roundly app. Once it's connected, everything works here too.")))
    } else {
      const connect = el('button', { type: 'button', class: 'btn-primary wa-connect' }, 'Connect WhatsApp') as HTMLButtonElement
      connect.addEventListener('click', () => void connectWhatsApp(connect, note, reload))
      card.append(connect, el('p', { class: 'muted wa-small' }, 'Meta opens a window: log in with Facebook, pick your business, type the work number and confirm on the phone. About 5 minutes, with the work phone beside you.'))
    }
    card.append(note, el('div', { class: 'feature-where' }, el('strong', {}, 'Where it appears'), el('span', {}, 'Chats, and what customers wrote back on Owed.')))
    return card
  }

  if (attention) {
    const words = c.sending_off ? (c.problem ?? 'Restored from the backup: check WhatsApp on the phone for anything sent, and any STOP, then switch sending back on.')
      : c.problem ?? "WhatsApp stopped talking to Roundly. The work phone's WhatsApp Business wasn't opened for 14 days, or Roundly was disconnected in the app."
    card.append(el('div', { class: 'wa-attention' }, el('p', {}, words)))
    if (c.sending_off) {
      const back = el('button', { type: 'button', class: 'btn-primary' }, 'Switch sending back on') as HTMLButtonElement
      back.addEventListener('click', () => { back.disabled = true; void act({ action: 'sending_on', version: c.version }, note, reload) })
      card.append(back)
    } else {
      card.append(el('ol', { class: 'wa-steps' }, el('li', {}, 'Open WhatsApp Business on the work phone.'), el('li', {}, 'Tap Check again here.')))
      const check = el('button', { type: 'button', class: 'btn-primary' }, c.status === 'connecting' ? 'Try again' : 'Check again') as HTMLButtonElement
      check.addEventListener('click', () => { check.disabled = true; check.textContent = 'Checking…'; void act({ action: 'check' }, note, reload) })
      card.append(check)
      if (c.problem_kind === 'token' || c.problem_kind === 'offboarded') {
        const again = el('button', { type: 'button', class: 'btn-secondary' }, 'Connect WhatsApp again') as HTMLButtonElement
        if (inIphoneApp()) again.disabled = true
        again.addEventListener('click', () => void connectWhatsApp(again, note, reload))
        card.append(again)
      }
    }
    card.append(el('p', { class: 'muted wa-small' }, "Until it's back, Send opens WhatsApp on your phone, and customers' new messages stay on the phone."))
  } else {
    if (row.history) {
      const h = row.history.state
      card.append(el('p', { class: 'muted wa-small' }, h === 'done' ? 'Your chats from the last 6 months are in Chats.' : h === 'declined' ? 'Your older chats stay on the phone (not shared with Roundly).'
        : h === 'failed' || h === 'too_late' ? "Couldn't bring in your older chats: they stay on the phone." : 'Bringing in your chats from WhatsApp. Keep WhatsApp Business open on the phone.'))
    }
    card.append(el('div', { class: 'wa-reminder' }, 'Open WhatsApp Business on the work phone at least every 14 days, or WhatsApp disconnects Roundly.'))
    if (row.words_possible) {
      const sw = toggle(c.voice_words, 'Voice notes as words')
      sw.addEventListener('click', () => { sw.disabled = true; void act({ action: 'voice_words', on: !c.voice_words }, note, reload) })
      card.append(el('div', { class: 'wa-voice' },
        el('div', {}, el('strong', {}, 'Voice notes as words'),
          el('p', { class: 'muted' }, `Customers' voice notes show as words too, to read between jobs. The recordings go to Groq, as your workers' voice notes do.${row.voice_notes ? ` ${row.voice_notes} voice note${row.voice_notes === 1 ? '' : 's'} in your chats so far.` : ''}`)),
        sw))
    }
    if (c.status === 'connected' && row.payments) card.append(paymentsPart(row.payments, note, reload))
    if (c.status === 'connecting') {
      const check = el('button', { type: 'button', class: 'btn-secondary' }, 'Check again') as HTMLButtonElement
      check.addEventListener('click', () => { check.disabled = true; void act({ action: 'check' }, note, reload) })
      card.append(el('p', {}, 'Connecting to WhatsApp…'), check)
    }
  }
  // Two taps: the first says what it does. Still there while a Disconnect couldn't reach WhatsApp, to try again.
  const off = el('button', { type: 'button', class: 'btn-danger wa-disconnect' }, c.status === 'disconnecting' ? 'Disconnect again' : 'Disconnect') as HTMLButtonElement
  if (c.status === 'disconnecting') card.append(el('p', { class: 'muted wa-small' }, "Roundly has stopped sending, but couldn't reach WhatsApp to finish disconnecting. Tap Disconnect again."))
  off.addEventListener('click', () => {
    if (off.dataset.armed !== '1') { off.dataset.armed = '1'; off.textContent = 'Tap again: Roundly stops sending and receiving WhatsApps'; return }
    off.disabled = true
    void act({ action: 'disconnect' }, note, reload, () => toast('WhatsApp disconnected from Roundly. To finish on the phone: WhatsApp Business, Settings, Account, Business Platform.'))
  })
  card.append(off)
  card.append(note)
  return card
}

// Meta's window (Embedded Signup, coexistence): the server starts a sign-up attempt, Facebook's own script opens the
// window, and the answer (Meta's code, and the WhatsApp account and number from the window's messages, believed only from
// Facebook's own addresses) goes back to the server, which checks it all with Meta itself (functions/lib/wa-setup.ts).
// autoLogAppEvents stays false: the privacy page promises no tracking, and the window needs only FB.login (Liam, 3 Nov 2026).
interface FbLogin { authResponse?: { code?: string } | null }
interface Fb { init(o: Record<string, unknown>): void; login(cb: (r: FbLogin) => void, o: Record<string, unknown>): void }
let sdk: Promise<Fb> | null = null
function facebook(appId: string): Promise<Fb> {
  sdk ??= new Promise<Fb>((resolve, reject) => {
    const w = window as unknown as { FB?: Fb; fbAsyncInit?: () => void }
    w.fbAsyncInit = () => { w.FB!.init({ appId, autoLogAppEvents: false, xfbml: false, version: 'v26.0' }); resolve(w.FB!) }
    const s = document.createElement('script')
    s.src = 'https://connect.facebook.net/en_US/sdk.js'
    s.async = true; s.defer = true; s.crossOrigin = 'anonymous'
    s.onerror = () => { sdk = null; reject(new Error("Couldn't reach Facebook. Check your signal and try again.")) }
    document.head.append(s)
  })
  return sdk
}

// The one Connect under way (Astra's code review): a new one ends the last, and Meta's window messages count only for it.
let attempt = 0
let stopListening: (() => void) | null = null

let windowOpen = false
async function connectWhatsApp(button: HTMLButtonElement, note: HTMLElement, reload: () => void): Promise<void> {
  // One Meta window at a time: a second Connect while one is open would mix up their answers (Astra's code review).
  if (windowOpen) { note.textContent = "Meta's window is still open. Finish there, or close it and tap Connect WhatsApp again."; return }
  stopListening?.()
  const mine = ++attempt
  button.disabled = true
  note.textContent = ''
  const was = button.textContent
  button.textContent = 'Opening Meta…'
  const restore = (words: string): void => { button.disabled = false; button.textContent = was; note.textContent = words }
  let start: { signup: string; appId: string; configId: string }
  try {
    start = (await changeNow<{ row: { signup: string; appId: string; configId: string } }>('POST', WA_PATH, { action: 'start' })).row
  } catch (err) {
    restore(err instanceof ApiError ? err.message : "Couldn't start. Check your signal and try again.")
    return
  }
  let fb: Fb
  try { fb = await facebook(start.appId) } catch (err) { restore((err as Error).message); return }
  if (mine !== attempt) return
  const session: { waba?: string; number?: string; ended?: string } = {}
  const listen = (e: MessageEvent): void => {
    if (mine !== attempt || !/^https:\/\/([a-z]+\.)?facebook\.com$/.test(e.origin)) return
    let data: { type?: unknown; event?: unknown; data?: { waba_id?: unknown; phone_number_id?: unknown } } | null = null
    try { data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data } catch { return }
    if (!data || data.type !== 'WA_EMBEDDED_SIGNUP') return
    const ev = String(data.event ?? '')
    if (ev.startsWith('FINISH')) {
      if (typeof data.data?.waba_id === 'string') session.waba = data.data.waba_id
      if (typeof data.data?.phone_number_id === 'string') session.number = data.data.phone_number_id
    } else if (ev === 'CANCEL' || ev === 'ERROR') session.ended = ev
  }
  window.addEventListener('message', listen)
  stopListening = () => window.removeEventListener('message', listen)
  // Held until Meta's window answers (closing it answers too); a reload of the page starts afresh.
  windowOpen = true
  fb.login((response) => {
    void (async () => {
      // Held until this window's code and number are paired and sent, with its listener gone (Astra's code review).
      try {
      if (mine !== attempt) return
      const code = response.authResponse?.code
      // The window's own messages can land just after the login answer.
      for (let i = 0; i < 20 && !(session.waba && session.number) && !session.ended; i++) await new Promise((r) => setTimeout(r, 250))
      window.removeEventListener('message', listen)
      if (mine !== attempt) return
      // The number may not come back from Meta's window (an existing WhatsApp Business app): Roundly finds it from the
      // account (Sonnet's WhatsApp review A6). The account itself is needed.
      if (!code || !session.waba) { restore(session.ended === 'CANCEL' || !code ? 'Connect was stopped before the end. Tap Connect WhatsApp to try again.' : "Meta didn't say which WhatsApp account. Tap Connect WhatsApp to try again."); return }
      button.textContent = 'Connecting…'
      try {
        await changeNow('POST', WA_PATH, { action: 'finish', signup: start.signup, code, waba_id: session.waba, ...(session.number ? { phone_number_id: session.number } : {}) })
        toast('WhatsApp connected. Bringing in your chats now.')
        reload()
      } catch (err) {
        restore(err instanceof ApiError ? err.message : "Couldn't finish connecting. Tap Connect WhatsApp to try again.")
        reload()
      }
      } finally {
        window.removeEventListener('message', listen)
        if (mine === attempt) windowOpen = false
      }
    })()
  }, { config_id: start.configId, response_type: 'code', override_default_response_type: true,
    extras: { setup: {}, featureType: 'whatsapp_business_app_onboarding', sessionInfoVersion: '3' } })
}
